import { useState, useRef, useEffect } from 'react';

/**
 * Multi-Select Dropdown Component
 *
 * Allows users to select multiple options from a dropdown with checkboxes.
 * Displays selected items as tags and shows a dropdown when clicked.
 */

export interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
}

export default function MultiSelect({
  label,
  options,
  selectedIds,
  onChange,
  placeholder = 'Select options...',
  helpText,
  disabled = false
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleOption = (optionId: string) => {
    if (selectedIds.includes(optionId)) {
      onChange(selectedIds.filter(id => id !== optionId));
    } else {
      onChange([...selectedIds, optionId]);
    }
  };

  const handleSelectAll = () => {
    onChange(options.map(opt => opt.id));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const selectedOptions = options.filter(opt => selectedIds.includes(opt.id));

  // Single option — nothing to choose, just show it as static text
  if (options.length === 1 && !disabled) {
    return (
      <div className="mb-6">
        <label className="mb-2 block text-sm font-semibold text-[#014876]">
          {label}
        </label>
        {helpText && <p className="mb-2 text-xs text-gray-500">{helpText}</p>}
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          {options[0].label}
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6" ref={dropdownRef}>
      <label className="mb-2 block text-sm font-semibold text-[#014876]">
        {label}
      </label>

      {helpText && <p className="mb-2 text-xs text-gray-500">{helpText}</p>}

      <div className="relative">
        {/* Dropdown trigger */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-left outline-none transition focus:border-transparent focus:ring-2 focus:ring-[#014876] ${
            disabled
              ? 'cursor-not-allowed bg-gray-100'
              : 'bg-white hover:border-gray-400'
          }`}
        >
          {selectedOptions.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {selectedOptions.map(option => (
                <span
                  key={option.id}
                  className="inline-flex items-center rounded bg-[#014876] px-2 py-0.5 text-xs text-white"
                >
                  {option.label}
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      handleToggleOption(option.id);
                    }}
                    className="ml-1 cursor-pointer hover:text-gray-300"
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        handleToggleOption(option.id);
                      }
                    }}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          )}
        </button>

        {/* Dropdown menu */}
        {isOpen && !disabled && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
            {/* Select All / Clear All buttons */}
            <div className="flex gap-2 border-b border-gray-200 p-2">
              <button
                type="button"
                onClick={handleSelectAll}
                className="flex-1 rounded px-2 py-1 text-xs font-medium text-[#014876] hover:bg-gray-100"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                className="flex-1 rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
              >
                Clear All
              </button>
            </div>

            {/* Options list */}
            <div className="py-1">
              {options.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No options available
                </div>
              ) : (
                options.map(option => (
                  <label
                    key={option.id}
                    className="flex cursor-pointer items-center px-3 py-2 hover:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(option.id)}
                      onChange={() => handleToggleOption(option.id)}
                      className="mr-2 rounded border-gray-300 text-[#014876] focus:ring-[#014876]"
                    />
                    <span className="text-sm text-gray-700">
                      {option.label}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          {selectedOptions.length} of {options.length} selected
        </p>
      )}
    </div>
  );
}
